
import axios from 'axios'
import type { JobStatus, JobCreatePayload } from '../shared-types'

// 🔧 Create axios instance with fallback base URL
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// 🔐 Inject Authorization token into headers (if present)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// 🚨 Handle unauthorized errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// 🔌 API METHODS

export const getDriverJobs = async (driverId: string) => {
  try {
    const response = await apiClient.get(`/drivers/${driverId}/jobs`)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const getDispatcherJobs = async (dispatcherId: string) => {
  try {
    const response = await apiClient.get(`/dispatchers/${dispatcherId}/jobs`)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const createJob = async (jobData: JobCreatePayload) => {
  try {
    const response = await apiClient.post('/jobs', jobData)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const acceptJob = async (jobId: string, driverId: string) => {
  try {
    const response = await apiClient.post(`/jobs/${jobId}/accept`, { driverId })
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const cancelJob = async (jobId: string) => {
  try {
    const response = await apiClient.post(`/jobs/${jobId}/cancel`)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const getDriverById = async (driverId: string) => {
  try {
    const response = await apiClient.get(`/drivers/${driverId}`)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const getJobById = async (jobId: string) => {
  try {
    const response = await apiClient.get(`/jobs/${jobId}`)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const updateJobStatus = async (jobId: string, status: JobStatus) => {
  try {
    const response = await apiClient.patch(`/jobs/${jobId}`, { status })
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

// Export the API client and methods as default
const api = {
  getDriverJobs,
  getDispatcherJobs,
  createJob,
  acceptJob,
  cancelJob,
  getDriverById,
  getJobById,
  updateJobStatus,
}

export default api
